package com.siriusxm.pia.operations.components

import androidx.compose.runtime.*
import com.siriusxm.pia.components.Column
import com.siriusxm.pia.components.button
import com.siriusxm.unifiedcontent.cmc.CMCService
import com.siriusxm.unifiedcontent.cmc.Monitor
import com.siriusxm.unifiedcontent.cmc.MonitorState
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Filter options for monitors viewer.
 */
enum class MonitorFilter(val label: String) {
    ALL("All"),
    ALERTING("Alerting"),
    WARNING("Warning"),
    OK("OK"),
    NO_DATA("No Data")
}

/**
 * Configuration for the MonitorsViewer component.
 */
class MonitorsViewerConfig {
    var client: CMCService? = null
    var tag: String? = null
    var showOnlyAlerting: Boolean = false
    var showFilter: Boolean = true
    var datadogBaseUrl: String = "https://app.datadoghq.com"

    internal var onMonitorClick: ((Monitor) -> Unit)? = null

    /**
     * Handler for when a monitor is clicked.
     */
    fun onMonitorClick(handler: (Monitor) -> Unit) {
        onMonitorClick = handler
    }
}

/**
 * A component for viewing DataDog monitors with filtering, pagination, and status-based sorting.
 *
 * Features:
 * - Status-based filtering (All, Alerting, Warning, OK, No Data)
 * - Sorted by priority (P1 first, nulls last), then by severity
 * - Monitor cards with status indicators and DataDog links
 * - Page-based "Load More" pagination using the CMC service's page/pageSize params
 *
 * @param config Configuration block for the viewer
 */
@Composable
fun MonitorsViewer(config: MonitorsViewerConfig.() -> Unit) {
    val viewerConfig = MonitorsViewerConfig().apply(config)
    val client = viewerConfig.client ?: return

    var monitors by remember { mutableStateOf<List<Monitor>>(emptyList()) }
    var filter by remember { mutableStateOf(if (viewerConfig.showOnlyAlerting) MonitorFilter.ALERTING else MonitorFilter.ALL) }
    var isLoading by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var currentPage by remember { mutableStateOf(0) }
    var pageCount by remember { mutableStateOf<Int?>(null) }

    val scope = rememberCoroutineScope()

    /**
     * Fetches monitors from the CMC service. When reset=true starts fresh from page 0;
     * when reset=false appends the next page to the existing list.
     */
    suspend fun loadMonitors(reset: Boolean = false) {
        isLoading = true
        error = null

        try {
            val tag = viewerConfig.tag
            if (tag == null) {
                error = "A tag must be provided"
                return
            }
            val page = if (reset) 0 else currentPage + 1
            val response = client.operations.getMonitors(tag = tag, page = page)
            val newMonitors = response.monitors.orEmpty()
            monitors = if (reset) newMonitors else monitors + newMonitors
            currentPage = page
            pageCount = response.pageCount
        } catch (e: Exception) {
            error = e.message ?: "Failed to load monitors"
        } finally {
            isLoading = false
        }
    }

    // Load monitors when tag changes
    LaunchedEffect(viewerConfig.tag) {
        loadMonitors(reset = true)
    }

    // Filter and sort monitors
    val filteredMonitors = remember(monitors, filter) {
        monitors
            .filter { monitor ->
                when (filter) {
                    MonitorFilter.ALL -> true
                    MonitorFilter.ALERTING -> monitor.status == MonitorState.ALERT || monitor.status == MonitorState.WARN
                    MonitorFilter.WARNING -> monitor.status == MonitorState.WARN
                    MonitorFilter.OK -> monitor.status == MonitorState.OK
                    MonitorFilter.NO_DATA -> monitor.status == MonitorState.NO_DATA
                }
            }
            .sortedWith(
                compareBy<Monitor> { monitor ->
                    // Sort by priority (lower is higher priority), nulls last
                    monitor.priority ?: Int.MAX_VALUE
                }.thenBy { monitor ->
                    // Then by status severity
                    when (monitor.status) {
                        MonitorState.ALERT -> 0
                        MonitorState.WARN -> 1
                        MonitorState.NO_DATA -> 2
                        MonitorState.OK -> 3
                        else -> 4
                    }
                }
            )
    }

    Style(OperationsStyles)

    Column(1.cssRem) {
        // Filter controls
        if (viewerConfig.showFilter && monitors.isNotEmpty()) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    marginBottom(10.px)
                }
            }) {
                MonitorFilter.entries.forEach { filterOption ->
                    val count = monitors.count { monitor ->
                        when (filterOption) {
                            MonitorFilter.ALL -> true
                            MonitorFilter.ALERTING -> monitor.status == MonitorState.ALERT || monitor.status == MonitorState.WARN
                            MonitorFilter.WARNING -> monitor.status == MonitorState.WARN
                            MonitorFilter.OK -> monitor.status == MonitorState.OK
                            MonitorFilter.NO_DATA -> monitor.status == MonitorState.NO_DATA
                        }
                    }

                    Span({
                        style {
                            padding(4.px, 12.px)
                            borderRadius(4.px)
                            cursor("pointer")
                            if (filter == filterOption) {
                                backgroundColor(Color("#1976d2"))
                                color(Color.white)
                            } else {
                                backgroundColor(Color("#e0e0e0"))
                            }
                        }
                        onClick { filter = filterOption }
                    }) {
                        Text("${filterOption.label} ($count)")
                    }
                }
            }
        }

        // Error message
        error?.let { errorMessage ->
            Div({
                style {
                    padding(10.px)
                    backgroundColor(Color("#ffebee"))
                    color(Color("#c62828"))
                    borderRadius(4.px)
                }
            }) {
                Text(errorMessage)
            }
        }

        // Loading indicator — only shown during initial load (not "Load More")
        if (isLoading && monitors.isEmpty()) {
            Div({
                style {
                    padding(20.px)
                    textAlign("center")
                    color(Color("#666"))
                }
            }) {
                Text("Loading monitors...")
            }
        }

        // Monitors list
        if (filteredMonitors.isNotEmpty()) {
            Div({
                classes(OperationsStyles.monitorsGrid)
            }) {
                filteredMonitors.forEach { monitor ->
                    MonitorCard(
                        monitor = monitor,
                        datadogBaseUrl = viewerConfig.datadogBaseUrl,
                        onClick = viewerConfig.onMonitorClick?.let { handler -> { handler(monitor) } }
                    )
                }
            }

            // Load more button — shown when there are additional pages to fetch
            val hasMorePages = pageCount != null && currentPage + 1 < pageCount!!
            if (hasMorePages) {
                Div({
                    style {
                        padding(10.px)
                        textAlign("center")
                    }
                }) {
                    button {
                        title = if (isLoading) "Loading..." else "Load More"
                        enabled = !isLoading
                        action {
                            scope.launch {
                                loadMonitors(reset = false)
                            }
                        }
                    }
                }
            }
        } else if (!isLoading && error == null && monitors.isNotEmpty()) {
            Div({
                style {
                    padding(20.px)
                    textAlign("center")
                    color(Color("#666"))
                }
            }) {
                Text("No monitors match the current filter")
            }
        } else if (!isLoading && error == null) {
            Div({
                style {
                    padding(20.px)
                    textAlign("center")
                    color(Color("#666"))
                }
            }) {
                Text("No monitors found")
            }
        }
    }
}

/**
 * A summary view showing only alerting monitors.
 *
 * Useful for embedding in dashboards or service overview pages.
 * Returns null content if there are no alerting monitors.
 *
 * @param monitors List of monitors to display
 * @param datadogBaseUrl Base URL for DataDog links
 */
@Composable
fun AlertingMonitorsSummary(
    monitors: List<Monitor>,
    datadogBaseUrl: String = "https://app.datadoghq.com"
) {
    val alertingMonitors = monitors.filter { it.status.isAlerting() }
        .sortedWith(
            compareBy<Monitor> { it.priority ?: Int.MAX_VALUE }
                .thenBy { if (it.status == MonitorState.ALERT) 0 else 1 }
        )

    if (alertingMonitors.isEmpty()) {
        return
    }

    Style(OperationsStyles)

    Div({
        classes(OperationsStyles.monitorsGrid)
    }) {
        alertingMonitors.forEach { monitor ->
            MonitorCard(
                monitor = monitor,
                datadogBaseUrl = datadogBaseUrl
            )
        }
    }
}
